'use client';
import { useWindowOpen } from '@repo/platform';
import { BoxButton, LottieWebPlayer, useWindowSize } from '@repo/ui';
import { CSSProperties, useMemo } from 'react';

import { CONTENT_FEATURES } from '@/lib/constants/landing-sections';
import { ContentFeature } from '@/lib/types/landing';

export const ContentSection = () => {
  return (
    <section className={'container-large container-padding-xlarge py-huge-medium w-full flex flex-col gap-huge-large items-center overflow-x-clip'}>
      {CONTENT_FEATURES.map((feature, index) => (
        <FeatureItem key={index} {...feature} />
      ))}
    </section>
  );
};

const FeatureItem = ({ title, description, buttonText, buttonLink, lottiePath }: ContentFeature) => {
  const { openWindow } = useWindowOpen();
  return (
    <div className={`w-full flex items-center compact:flex-col compact:gap-normal-large flex-row-reverse justify-between`}>
      <MockUp lottiePath={lottiePath} />
      <div className={`container-xsmall py-normal-lg text-left compact:text-center compact:max-w-[360px] compact:py-normal-large`}>
        <h2 className={'text-neutral-foreground-main text-3xl font-bold tracking-tight leading-heading'}>{title}</h2>
        <p className={'font-semibold text-neutral-foreground-lowest mt-normal-medium mb-0 text-lg leading-heading'}>{description}</p>
        <BoxButton
          size={'medium'}
          className={'rounded-full mt-normal-large'}
          onClick={() => {
            openWindow(buttonLink);
          }}
        >
          {buttonText}
        </BoxButton>
      </div>
    </div>
  );
};

type ImageProps = {
  lottiePath: string;
};

export const MockUp = ({ lottiePath }: ImageProps) => {
  const { isOverMobile, isOverTablet } = useWindowSize();
  const { width, height } = useMemo(() => {
    if (isOverTablet) return { width: 277.74, height: 600 };
    if (isOverMobile) return { width: 231.45, height: 500 };
    return { width: 203.67, height: 440 };
  }, [isOverMobile, isOverTablet]);

  // frame 위치 및 크기 계산
  const frameWidth = 944;
  const frameHeight = 1384;
  const frameLeft = 32;
  const frameTop = 23;
  const screenWidth = 524;
  const screenHeight = 1132;

  const frameStyle: CSSProperties = {
    position: 'absolute',
    zIndex: 20,
    margin: '0 auto',
    top: `calc(-1px * ${height} * ${frameTop} / ${screenHeight})`,
    left: `calc(-1px * ${width} * ${frameLeft} / ${screenWidth})`,
    width: `calc(100% * (${frameWidth} / ${screenWidth}))`,
    height: `calc(100% * (${frameHeight} / ${screenHeight}))`,
    lineHeight: 0
  };

  return (
    <div className={'relative pointer-events-none leading-none w-[203.67px] h-[440px] ss:w-[231.45px] ss:h-[500px] md:w-[277.74px] md:h-[600px]'}>
      <div className={'absolute mx-auto z-20 leading-[0]'} style={frameStyle}>
        <img src="/images/iPhone16-frame.png" className={'w-full h-full'} alt="목업용 이미지" />
      </div>
      <div className="absolute left-0 top-0 w-full h-full bg-white leading-none">
        <LottieWebPlayer src={lottiePath} />
      </div>
    </div>
  );
};
